import React, { useState } from "react";
import { Button, Form, Input, Select } from "antd";
import { useSelector } from "react-redux";
import {
  emailValidation,
  rolesToExclude,
  validatePhoneNumber,
} from "../Common/Utils";
import { jwtRegister } from "niche-user-fe-service";
import {
  addUserFleetsApiUrl,
  existingUserDetailsUrl,
  organisationAddUserApiUrl,
  userJwtRegisterUrl,
} from "../Common/endpoints";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FORMS, ROUTES } from "../Common/Constants";
import { postApis } from "../Common/apis";
import { useRef } from "react";

const { Option } = Select;

const RegisterForm = ({ fleetList }) => {
  const roles = useSelector((state) => state.auth?.roles);
  const userToken = localStorage.getItem("userToken");
  const navigate = useNavigate();
  const registrationFormRef = useRef(null);
  const [optionsSelected, setOptionsSelected] = useState([]);
  const [existUser, setExistUser] = useState(null);
  const [message, setMessage] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const [inputDisable, setInputDisable] = useState(true);

  const fleetMangerId = roles.find((role) => role.name === "Fleet Manager")?.id;

  const handleChange = (value) => {
    setOptionsSelected(value);
  };

  const filteredRoles = roles.filter(
    (role) => !rolesToExclude()?.includes(role.name)
  );

  const createUserFleets = async (userId, fleetIds) => {
    await postApis(addUserFleetsApiUrl, { userId, fleetIds }, userToken)
      .then((response) => {
        setButtonLoading(false);
        navigate(ROUTES.PROFILE.link);
        toast.success(FORMS.ADD_USER.REQUIRED_FIELDS.SUCCESS);
      })
      .catch((error) => setMessage(error?.response?.data?.message));
  };

  const OrganisationAddUser = async (userId) => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const orgId = userDetails?.mainOrganisation?.id;
    await postApis(
      organisationAddUserApiUrl + `?userId=${userId}&orgId=${orgId}`,
      {},
      userToken
    )
      .then((response) => {
        setButtonLoading(false);
        navigate(ROUTES.PROFILE.link);
        toast.success(FORMS.ADD_USER.REQUIRED_FIELDS.SUCCESS);
      })
      .catch((error) => setMessage(error?.response?.data?.message));
  };

  const onFinish = async (values) => {
    setButtonLoading(true);
    if (existUser?.userId) {
      OrganisationAddUser(existUser.userId);
    } else {
      const { fleetIds, ...userData } = values;
      await jwtRegister(userJwtRegisterUrl, userData, userToken)
        .then((response) => {
          if (fleetIds && response?.data?.userId) {
            createUserFleets(response?.data?.userId, fleetIds);
          } else {
            setButtonLoading(false);
            navigate(ROUTES.PROFILE.link);
            toast.success(FORMS.ADD_USER.REQUIRED_FIELDS.SUCCESS);
          }
        })
        .catch((error) => {
          setButtonLoading(false);
          setMessage(error?.response?.data?.message);
        });
    }
  };

  const userDetailsClear = () => {
    setExistUser(null);
    setOptionsSelected([]);
    registrationFormRef.current?.setFieldsValue({
      firstName: null,
      lastName: null,
      phoneNumber: null,
      password: null,
      roleIds: null,
      fleetIds: null,
    });
  }

  const onEmailChange = async () => {
    setMessage(null);
    const { email } = registrationFormRef.current?.getFieldValue();
    if (email && emailValidation(email)) {
      setInputDisable(true);
      await postApis(existingUserDetailsUrl, { email }, userToken)
        .then((response) => {
          setInputDisable(response?.data?.userId > 0);
          if (response?.data?.userId) {
            setExistUser({
              userId: response?.data?.userId,
              ...response?.data?.details,
            });
            registrationFormRef.current?.setFieldsValue({
              ...response?.data?.details,
            });
          } else {
            userDetailsClear();
          }
        })
        .catch((error) => {
          setInputDisable(error?.response?.data?.userId > 0);
          setMessage(error?.response?.data?.message);
          userDetailsClear();
        });
    }
  };

  return (
    <div data-testid="registerForm">
      <div className="error">{message}</div>
      <Form
        name="register"
        block="true"
        ref={registrationFormRef}
        onFinish={onFinish}
        className="register mt1"
        validateMessages={{
          required: FORMS.ADD_USER.REQUIRED_FIELDS.BLANK_ERROR,
        }}
      >
        <Form.Item
          name="email"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
            },
            {
              type: "email",
              message: FORMS.ADD_USER.REQUIRED_FIELDS.INVALID_ERROR,
            },
          ]}
        >
          <Input
            placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.EMAIL}
            size="large"
            onChange={onEmailChange}
          />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          rules={[
            {
              required: true,
            },
            {
              validator: validatePhoneNumber,
            },
          ]}
        >
          <Input
            placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.PHONE}
            size="large"
            disabled={inputDisable}
          />
        </Form.Item>

        <Form.Item
          name="firstName"
          rules={[
            {
              required: true,
            },
          ]}
          style={{
            display: "inline-block",
            width: "calc(50% - 8px)",
          }}
        >
          <Input
            placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.FIRST_NAME}
            size="large"
            disabled={inputDisable}
          />
        </Form.Item>
        <Form.Item
          name="lastName"
          rules={[
            {
              required: true,
            },
          ]}
          style={{
            display: "inline-block",
            width: "calc(50% - 8px)",
            margin: "0px 0px 0 16px",
          }}
        >
          <Input
            placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.LAST_NAME}
            disabled={inputDisable}
            size="large"
          />
        </Form.Item>

        {!existUser?.userId && (
          <>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input
                placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.PASSWORD}
                size="large"
                disabled={inputDisable}
                type="password"
              />
            </Form.Item>

            <Form.Item
              name="roleId"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.ROLE}
                mode="multiple"
                data-testid="roles-dropdown"
                onChange={handleChange}
                disabled={inputDisable}
                size="large"
              >
                {filteredRoles.map((r, index) => {
                  return (
                    <Option
                      key={index}
                      disabled={
                        optionsSelected.length > 0
                          ? optionsSelected.includes(r.id)
                            ? false
                            : true
                          : false
                      }
                      value={r.id}
                    >
                      {r.name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </>
        )}

        {optionsSelected.includes(fleetMangerId) ? (
          <Form.Item
            name="fleetIds"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              placeholder={FORMS.ADD_USER.REQUIRED_FIELDS.FLEET}
              mode="multiple"
              disabled={inputDisable}
              size="large"
            >
              {fleetList.map((f, index) => {
                return (
                  <Option key={index} value={f.fleetId}>
                    {f.fleetName}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        ) : null}

        <Button
          type="primary"
          htmlType="submit"
          className="ant-btn-submit mt1"
          loading={buttonLoading}
          disabled={!existUser?.userId && inputDisable}
        >
          {existUser?.userId
            ? FORMS.ADD_USER.REQUIRED_FIELDS.ADD_ORG
            : FORMS.ADD_USER.REQUIRED_FIELDS.SUBMIT}
        </Button>
      </Form>
    </div>
  );
};
export default RegisterForm;
